import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Star, Award, Users, Lock as LockIcon, BookOpen, Headphones, ArrowLeft } from "lucide-react";
import { api, getChildId } from "../lib/api";
import { loadRecordings, play, setAudioPrefs } from "../lib/audio";
import { ParentGate } from "../components/ParentGate";
import { Avatar } from "../components/Avatar";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { usePremiumPrompt, CrownBadge } from "../components/billing/PremiumPrompt";

const Node = ({ s, onTap }) => {
  const locked = s.state === "locked" || s.state === "premium";
  return (
    <motion.button data-testid={`map-node-${s.story_number}`} onClick={() => onTap(s)} whileHover={{ scale: locked ? 1 : 1.08 }} whileTap={{ scale: 0.92 }}
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: s.story_number * 0.06 }}
      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${s.map_x}%`, top: `${s.map_y}%` }}>
      <div className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center border-4 shadow-lg ${locked ? "bg-stone-300 border-stone-400 grayscale" : s.state === "completed" ? "bg-[var(--jade)] border-amber-300" : "bg-[var(--vermilion)] border-amber-300 floaty"}`}>
        {locked ? <Lock className="text-stone-600" size={28} /> : <span className="font-serif-tc font-black text-white text-2xl sm:text-3xl">{s.story_number}</span>}
        {s.state === "premium" && <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white border-2 border-amber-300 flex items-center justify-center" data-testid={`map-node-premium-${s.story_number}`}><CrownBadge size={18} /></span>}
        {s.state === "unlocked" && <span className="absolute inset-0 rounded-full animate-ping bg-red-400/30" />}
      </div>
      <div className={`mt-1 px-3 py-1 rounded-full text-sm sm:text-base font-bold font-serif-tc whitespace-nowrap shadow ${locked ? "bg-stone-100/90 text-stone-500" : "bg-[#FFFDF7]/95 text-[var(--tea)]"}`}>{s.map_location}</div>
      {s.state === "completed" && (
        <div className="flex mt-1 bg-white/90 rounded-full px-2 py-0.5 shadow" data-testid={`map-node-stars-${s.story_number}`}>
          {[1, 2, 3].map((i) => <Star key={i} size={24} className={i <= s.stars ? "text-amber-400" : "text-stone-300"} fill="currentColor" />)}
        </div>
      )}
    </motion.button>
  );
};

const MissionSheet = ({ story, onClose, onStart, onListen }) => (
  <Dialog open={!!story} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg p-0 overflow-hidden rounded-3xl border-4 border-amber-200" data-testid="mission-dialog">
      {story && (
        <>
          <img src={story.thumbnail} alt="" className="w-full h-44 object-cover" />
          <div className="p-6">
            <DialogTitle className="font-serif-tc font-black text-4xl text-[var(--vermilion)]">{story.story_title}</DialogTitle>
            <div className="grid gap-3 mt-5">
              {story.missions?.filter((m) => m.subject_type !== "math").map((m) => (
                <button key={m.subject_type} data-testid={`mission-${m.subject_type}-btn`} disabled={!m.available} onClick={onStart}
                  className={`kid-btn flex items-center justify-center gap-3 text-2xl font-serif-tc ${m.available ? "bg-[var(--vermilion)] text-white" : "bg-stone-200 text-stone-500"}`}>
                  <BookOpen /> {m.label_zh}
                  {!m.available && <LockIcon size={20} />}
                </button>
              ))}
              <button data-testid="mission-listen-btn" onClick={onListen} className="kid-btn flex items-center justify-center gap-3 text-2xl font-serif-tc bg-white border-2 border-[var(--line)] text-[var(--tea)]">
                <Headphones /> 只聽故事
              </button>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export default function WorldMap() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [gate, setGate] = useState(false);
  const [page, setPage] = useState(1);
  const nav = useNavigate();
  const childId = getChildId();

  useEffect(() => {
    if (!childId) return nav("/profiles");
    loadRecordings();
    api.get(`/children/${childId}/map`).then(({ data }) => {
      setData(data);
      const cur = data.stories.find((s) => s.state === "unlocked");
      setPage(cur?.map_page || 1);
      setAudioPrefs(data.child.settings);
    }).catch(() => nav("/profiles"));
  }, [childId, nav]);

  const [prompt, premiumEl] = usePremiumPrompt();
  const tap = (s) => {
    if (s.state === "premium") return prompt();
    play(s.map_location);
    if (s.state !== "locked") setSel(s);
  };

  if (!data) return <div className="min-h-screen paper-bg" />;
  const { child } = data;
  const pages = [...new Set(data.stories.map((s) => s.map_page || 1))].sort();
  return (
    <div className="min-h-screen bg-[#e9dcc0] flex flex-col" data-testid="world-map-page">
      <header className="flex items-center justify-between px-4 sm:px-8 py-3 bg-amber-100/90 backdrop-blur-md border-b-2 border-amber-300/80 z-10">
        <div className="flex items-center gap-2">
          <button data-testid="map-halls-btn" onClick={() => nav("/halls")} className="w-14 h-14 rounded-full bg-white border-2 border-amber-300 text-[var(--tea)] flex items-center justify-center shrink-0"><ArrowLeft size={26} /></button>
          <button data-testid="map-switch-child-btn" onClick={() => nav("/profiles")} className="flex items-center gap-3">
            <span className="text-4xl w-14 h-14 rounded-full bg-white border-4 border-amber-300 flex items-center justify-center overflow-hidden"><Avatar value={child.avatar} /></span>
            <span className="font-serif-tc text-2xl font-bold text-[var(--tea)] hidden lg:block">{child.nickname}</span>
          </button>
        </div>
        <div className="flex gap-2" data-testid="map-page-tabs">
          {pages.map((p) => (
            <button key={p} data-testid={`map-page-${p}`} onClick={() => setPage(p)}
              className={`h-14 px-5 rounded-full font-serif-tc text-xl font-black border-2 ${page === p ? "bg-[var(--vermilion)] text-white border-transparent" : "bg-white border-amber-300 text-[var(--tea)]"}`}>
              第{"一二三四五六七八九十"[p - 1]}冊
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-5 h-14 rounded-full bg-white border-2 border-amber-300 text-2xl font-black text-amber-600" data-testid="map-star-count">
            <Star fill="currentColor" /> {child.stars}
          </div>
          <button data-testid="map-listen-btn" onClick={() => nav("/listen")} className="h-14 px-5 rounded-full bg-[var(--vermilion)] text-white flex items-center gap-2 shadow-md font-serif-tc font-black text-lg"><Headphones size={24} /><span className="hidden sm:inline">只聽故事</span></button>
          <button data-testid="map-rewards-btn" onClick={() => nav("/rewards")} className="w-14 h-14 rounded-full bg-[var(--jade)] text-white flex items-center justify-center shadow-md"><Award size={28} /></button>
          <button data-testid="map-parent-btn" onClick={() => setGate(true)} className="w-14 h-14 rounded-full bg-white border-2 border-stone-300 text-stone-500 flex items-center justify-center"><Users size={22} /></button>
        </div>
      </header>
      <div className="flex-1 flex items-center p-2 sm:p-6 overflow-auto">
        <div className="relative w-full aspect-[3/2] min-w-[720px] mx-auto rounded-[2rem] overflow-hidden border-8 border-[#c9a56b] shadow-2xl" style={{ maxWidth: "min(1400px, calc((100vh - 130px) * 1.5))" }}>
          <img src={data.map_images?.[page] || data.map_image} alt="冒險地圖" className="absolute inset-0 w-full h-full object-cover" />
          <div className="cloud absolute top-[8%] text-6xl opacity-80 pointer-events-none">☁️</div>
          <div className="cloud absolute top-[30%] text-5xl opacity-70 pointer-events-none" style={{ animationDelay: "-30s" }}>☁️</div>
          {data.stories.filter((s) => (s.map_page || 1) === page).map((s) => <Node key={s.story_id} s={s} onTap={tap} />)}
        </div>
      </div>
      <MissionSheet story={sel} onClose={() => setSel(null)} onStart={() => nav(`/lesson/${sel.story_id}`)} onListen={() => nav(`/listen?start=${sel.story_id}`)} />
      <ParentGate open={gate} onOpenChange={setGate} onPass={() => nav("/parent")} />
      {premiumEl}
    </div>
  );
}
